import React, { FC } from 'react';

import { useIntl } from 'react-intl';
import { useNavigate } from 'react-router-dom';

import { ActionButtonSizes, ActionButtonVariants } from 'components/action-button';
import useMediaQuery from 'hooks/use-media-query';
import { useFeaturesEarningCalculations } from 'state/loyalty/hooks/use-feature-earning-calculations';
import { useFeaturesLoyaltyTiersContent } from 'state/loyalty/hooks/use-feature-loyalty-tiers-content';
import { useLoyaltyUser } from 'state/loyalty/hooks/use-loyalty-user';
import { useLoyaltyUserTier } from 'state/loyalty/hooks/use-loyalty-user-tier';

import {
  AuthenticatedTierContainer,
  HowItWorksButton,
  TierDetailsContainer,
  TierIcon,
  TierPointBar,
  TierProgress,
  TierStart,
  TierTarget,
  UserTier,
  UserTierPoints,
} from './styled';
import { nextTierMap } from './utils';

interface ILoyaltyTiersAuthenticatedBannerProps {
  displayRewardsLink?: boolean;
}

const AuthenticatedTiersBanner: FC<ILoyaltyTiersAuthenticatedBannerProps> = ({
  displayRewardsLink,
}) => {
  const { loading: tiersContentLoading, data: tiersData } = useFeaturesLoyaltyTiersContent();
  const { loading: earningCalculationsLoading, data: earningCalculations } =
    useFeaturesEarningCalculations();
  const { loyaltyUser } = useLoyaltyUser();
  const { loyaltyTierKey: userTier } = useLoyaltyUserTier();

  const { formatMessage } = useIntl();
  const navigate = useNavigate();
  const isMobile = useMediaQuery('mobile');

  if (!loyaltyUser || tiersContentLoading || earningCalculationsLoading) {
    return null;
  }

  const userPoints = loyaltyUser.points || 0;

  // The next tier that the user can achieve. We use this to get the entrance point needed
  // If this is null it means that we are on the top tier and the progress bar should not show
  const userNextTier = nextTierMap[userTier];

  const nextTierPoints = userNextTier
    ? earningCalculations?.loyaltyTiersEntranceBehaviorConfigurations?.[0]?.pointsNeeded
    : 0;

  const tierConfig = tiersData?.loyaltyTiersUIConfig?.find(
    config => config?.loyaltyTiersUIConfigID === userTier
  );

  // If Tiers have not been configured in Sanity, don't render anything
  if (!tierConfig) {
    return null;
  }

  const handleClick = () => {
    if (!displayRewardsLink) {
      return navigate(tiersData?.loyaltyTiersHowItWorksButtonLink?.locale!);
    }
    navigate(tiersData?.loyaltyTiersRewardsButtonLink?.locale!);
  };

  const ActionButton = (
    <HowItWorksButton
      size={ActionButtonSizes.SMALL}
      data-testid="authenticated-tier-banner-how-it-works-button"
      variant={ActionButtonVariants.OUTLINE}
      onClick={handleClick}
    >
      {displayRewardsLink
        ? tiersData?.loyaltyTiersRewardsButtonText?.locale
        : tiersData?.loyaltyTiersHowItWorksButtonText?.locale}
    </HowItWorksButton>
  );

  return (
    <AuthenticatedTierContainer data-testid="authenticated-tier-banner">
      <TierDetailsContainer>
        <TierIcon image={tierConfig?.loyaltyTiersUIConfigIcon?.locale} alt="" objectFitContain />
        <UserTier accentColor={!nextTierPoints}>
          {formatMessage(
            { id: 'loyaltyTiersYouAre' },
            { tierName: tierConfig?.loyaltyTiersUIConfigName?.locale }
          )}
          <UserTierPoints>{userPoints}</UserTierPoints>
        </UserTier>
        {!tiersContentLoading && isMobile && ActionButton}
      </TierDetailsContainer>
      {nextTierPoints ? (
        <TierPointBar>
          <TierProgress progress={Math.min((userPoints / nextTierPoints) * 100, 100)} />
          <TierStart value={0} />
          <TierTarget value={nextTierPoints} />
        </TierPointBar>
      ) : null}
      {!tiersContentLoading && !isMobile && ActionButton}
    </AuthenticatedTierContainer>
  );
};

export default AuthenticatedTiersBanner;
